import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import { useSavedProperties } from '../hooks/useSavedProperties';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Search, MapPin, Home, Building2, Briefcase, Users, Star, ArrowRight, CheckCircle, ChevronRight, IndianRupee, Phone, ClipboardList, Eye, BookOpen, Calendar, Clock } from 'lucide-react';

const API = '/api';

const CATEGORIES = [
  { label: 'Apartments', icon: Home, query: { property_type: 'apartment' }, color: 'bg-blue-50 text-blue-700' },
  { label: 'Villas', icon: Star, query: { property_type: 'villa' }, color: 'bg-amber-50 text-amber-700' },
  { label: 'PG / Hostels', icon: Users, query: { property_category: 'pg' }, color: 'bg-green-50 text-green-700' },
  { label: 'Office Spaces', icon: Briefcase, query: { property_type: 'office' }, color: 'bg-purple-50 text-purple-700' },
  { label: 'Commercial', icon: Building2, query: { property_category: 'commercial' }, color: 'bg-rose-50 text-rose-700' },
  { label: 'Industrial', icon: Building2, query: { property_category: 'industrial' }, color: 'bg-slate-50 text-slate-700' },
];

const STEPS = [
  { step: '01', title: 'Search', desc: 'Use our smart filters to find your perfect rental in any city.' },
  { step: '02', title: 'Contact', desc: 'Connect directly with owners and brokers — no middlemen.' },
  { step: '03', title: 'Move In', desc: 'Finalize the deal and move into your new home with ease.' },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSaved, toggleSave } = useSavedProperties();
  const [searchCity, setSearchCity] = useState('');
  const [searchType, setSearchType] = useState('');
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ active_listings: 0, cities_covered: 0, verified_owners: 0 });
  const [requirements, setRequirements] = useState([]);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [revealedContact, setRevealedContact] = useState({});

  useEffect(() => {
    fetch(`${API}/properties/featured`)
      .then(r => r.json()).then(data => setFeatured(Array.isArray(data) ? data : []))
      .catch(() => {});
    fetch(`${API}/stats`)
      .then(r => r.json())
      .then(data => setStats({ active_listings: data.active_listings || 0, cities_covered: data.cities_covered || 0, verified_owners: data.verified_owners || 0 }))
      .catch(() => {});
    fetch(`${API}/requirements?limit=6`)
      .then(r => r.json())
      .then(data => setRequirements(data.requirements || []))
      .catch(() => {});
    fetch(`${API}/blogs/latest`)
      .then(r => r.json())
      .then(data => setLatestBlogs(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchCity) params.set('city', searchCity);
    if (searchType) params.set('property_category', searchType);
    navigate(`/search?${params.toString()}`);
  };

  const handleCategory = (cat) => {
    const params = new URLSearchParams(cat.query);
    navigate(`/search?${params.toString()}`);
  };

  const handleRevealContact = async (req) => {
    if (!user) { navigate('/login'); return; }
    if (user.role === 'tenant') {
      toast.info('Post your own requirement to get contacted by owners!');
      navigate('/post-requirement');
      return;
    }
    if (revealedContact[req.requirement_id]) return;
    setRevealedContact(prev => ({ ...prev, [req.requirement_id]: 'loading' }));
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API}/requirements/${req.requirement_id}/contact`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setRevealedContact(prev => ({ ...prev, [req.requirement_id]: data }));
    } catch {
      setRevealedContact(prev => { const n = { ...prev }; delete n[req.requirement_id]; return n; });
      toast.error('Could not load contact details. Please try again.');
    }
  };

  const handleToggleSave = async (propertyId) => {
    if (!user) { navigate('/login'); return; }
    try {
      const saved = await toggleSave(propertyId);
      toast.success(saved ? 'Saved to favorites!' : 'Removed from saved');
    } catch {
      toast.error('Please login to save properties');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Rent Mitra - Find Rental Properties in India</title>
        <meta name="description" content="India's complete rental marketplace. Find flats, PGs, commercial spaces and more. List your property for free." />
        <link rel="canonical" href={`${window.location.origin}/`} />
        <meta property="og:title" content="Rent Mitra - Your Complete Rental Marketplace" />
        <meta property="og:description" content="Find rental properties across India. Residential, Commercial, PG listings with verified owners." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/`} />
        <meta property="og:image" content="https://images.pexels.com/photos/4609051/pexels-photo-4609051.jpeg?w=1200" />
        <meta property="og:site_name" content="Rent Mitra" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rent Mitra - Find Rental Properties in India" />
        <meta name="twitter:description" content="Find rental properties across India. Residential, Commercial, PG listings." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "Rent Mitra",
          "url": window.location.origin,
          "description": "India's complete rental marketplace for flats, PGs, and commercial spaces.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${window.location.origin}/search?city={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I find rental properties on Rent Mitra?",
              "acceptedAnswer": { "@type": "Answer", "text": "Use our search to filter by city, BHK type, budget, and property category. You can view results in grid or map view and contact owners directly." }
            },
            {
              "@type": "Question",
              "name": "Is Rent Mitra free to use for tenants?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes, Rent Mitra is completely free for tenants. Browse listings, contact owners, and save properties without any charges or brokerage fees." }
            },
            {
              "@type": "Question",
              "name": "How can I list my property on Rent Mitra?",
              "acceptedAnswer": { "@type": "Answer", "text": "Register as an owner or broker, go to List Your Property, fill in the details, and submit for admin review. Your listing goes live within 24 hours." }
            },
            {
              "@type": "Question",
              "name": "Does Rent Mitra have PG and hostel listings?",
              "acceptedAnswer": { "@type": "Answer", "text": "Yes, Rent Mitra has dedicated PG and hostel listings across major Indian cities with details on food, sharing type, and occupant preferences." }
            }
          ]
        })}</script>
      </Helmet>
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ minHeight: '520px' }}>
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/4609051/pexels-photo-4609051.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-2xl">
            <span className="inline-block bg-blue-600/20 border border-blue-400/30 text-blue-200 text-xs font-semibold px-3 py-1.5 rounded-full mb-5 uppercase tracking-wider">
              India's Trusted Rental Portal
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5" style={{fontFamily:'Outfit,sans-serif'}}>
              Find Your Perfect<br />
              <span className="text-amber-400">Rental Home</span>
            </h1>
            <p className="text-slate-300 text-lg mb-8">
              Find genuine rental properties across India. Connect directly with owners and brokers.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 shadow-2xl" data-testid="hero-search-form">
              <div className="flex items-center gap-2 flex-1 px-3 py-1.5">
                <MapPin className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Enter city, area or pincode..."
                  value={searchCity}
                  onChange={e => setSearchCity(e.target.value)}
                  className="flex-1 outline-none text-slate-900 text-sm placeholder-slate-400 bg-transparent"
                  data-testid="hero-city-input"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 border-l border-slate-100 min-w-[160px]">
                <Home className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <select
                  value={searchType}
                  onChange={e => setSearchType(e.target.value)}
                  className="flex-1 outline-none text-slate-700 text-sm bg-transparent"
                  data-testid="hero-type-select"
                >
                  <option value="">All Types</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                  <option value="pg">PG / Hostel</option>
                  <option value="industrial">Industrial</option>
                </select>
              </div>
              <button
                type="submit"
                className="bg-blue-700 hover:bg-blue-800 text-white px-6 py-3 rounded-xl font-semibold text-sm flex items-center gap-2 transition-colors flex-shrink-0"
                data-testid="hero-search-button"
              >
                <Search className="w-4 h-4" />Search
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-blue-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { value: stats.active_listings > 0 ? stats.active_listings.toLocaleString('en-IN') + '+' : '—', label: 'Active Listings' },
              { value: stats.cities_covered > 0 ? stats.cities_covered.toLocaleString('en-IN') + '+' : '—', label: 'Cities Covered' },
              { value: stats.verified_owners > 0 ? stats.verified_owners.toLocaleString('en-IN') + '+' : '—', label: 'Verified Owners' },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl sm:text-3xl font-bold text-white" style={{fontFamily:'Outfit,sans-serif'}}>{s.value}</div>
                <div className="text-blue-200 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-2 block">Browse by Type</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>What are you looking for?</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.label}
                onClick={() => handleCategory(cat)}
                className={`flex flex-col items-center gap-3 p-5 rounded-2xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-200 bg-white group`}
                data-testid={`category-${cat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${cat.color}`}>
                  <cat.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-semibold text-slate-700 text-center leading-tight">{cat.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      {featured.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <span className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-2 block">Top Picks</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Featured Properties</h2>
              </div>
              <button
                onClick={() => navigate('/search')}
                className="hidden sm:flex items-center gap-1.5 text-blue-700 font-semibold text-sm hover:gap-2.5 transition-all"
                data-testid="view-all-properties"
              >
                View All <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-properties">
              {featured.slice(0, 6).map(p => (
                <PropertyCard key={p.property_id} property={p} isSaved={isSaved(p.property_id)} onToggleSave={handleToggleSave} />
              ))}
            </div>
            <div className="text-center mt-8 sm:hidden">
              <button onClick={() => navigate('/search')} className="text-blue-700 font-semibold flex items-center gap-1.5 mx-auto">
                View All Properties <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-2 block">Simple Process</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>How Rent Mitra Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.step} className="text-center">
                <div className="w-16 h-16 bg-blue-700 text-white rounded-2xl flex items-center justify-center mx-auto mb-5 text-xl font-bold shadow-lg" style={{fontFamily:'Outfit,sans-serif'}}>
                  {s.step}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily:'Outfit,sans-serif'}}>{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Live Requirement Board ── */}
      <section className="py-16 bg-slate-900" data-testid="live-requirements-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-green-400 mb-3">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                Live Board
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white" style={{fontFamily:'Outfit,sans-serif'}}>
                Active Rental Requirements
              </h2>
              <p className="text-slate-400 text-sm mt-2">
                Tenants looking for properties — reach out directly and close deals faster
              </p>
            </div>
            <Link
              to="/post-requirement"
              className="flex items-center gap-2 px-5 py-2.5 border border-slate-600 hover:border-green-500 text-slate-300 hover:text-green-400 rounded-xl text-sm font-semibold transition-all whitespace-nowrap group"
              data-testid="view-all-requirements-btn"
            >
              <ClipboardList className="w-4 h-4" />
              View All Requirements
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          {requirements.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No active requirements yet.</p>
              <Link to="/post-requirement" className="text-green-400 text-sm font-semibold mt-2 inline-block hover:underline">
                Be the first to post one
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-requirements-grid">
              {requirements.map(req => {
                const budgetStr = req.budget_min && req.budget_max
                  ? `₹${Number(req.budget_min).toLocaleString('en-IN')} – ₹${Number(req.budget_max).toLocaleString('en-IN')}/mo`
                  : req.budget_max
                  ? `Up to ₹${Number(req.budget_max).toLocaleString('en-IN')}/mo`
                  : 'Budget flexible';
                const contactData = revealedContact[req.requirement_id];
                const isLoading = contactData === 'loading';
                const isRevealed = contactData && contactData !== 'loading';
                const occupantColor = {
                  Family: 'bg-blue-900/60 text-blue-300',
                  Bachelor: 'bg-green-900/60 text-green-300',
                }[req.occupant_type?.split(' ')[0]] || 'bg-slate-700 text-slate-300';

                return (
                  <div
                    key={req.requirement_id}
                    className="bg-slate-800 border border-slate-700 hover:border-green-500/50 rounded-2xl p-5 transition-all duration-200 hover:shadow-xl hover:shadow-green-900/20 group"
                    data-testid={`home-req-card-${req.requirement_id}`}
                  >
                    {/* Card header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-green-900/50 border border-green-700/40 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-green-800/50 transition-colors">
                        <Home className="w-4.5 h-4.5 text-green-400" style={{width:'18px',height:'18px'}} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-white text-sm leading-tight">
                          Looking for {req.property_type} in {req.preferred_location || req.city}
                        </h4>
                        <p className="text-slate-400 text-xs mt-0.5 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{req.city}
                          {req.furnishing && <span className="ml-1 text-slate-500">· {req.furnishing}</span>}
                        </p>
                      </div>
                      {req.occupant_type && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0 ${occupantColor}`}>
                          {req.occupant_type.split(' ')[0]}
                        </span>
                      )}
                    </div>

                    {/* Budget */}
                    <div className="flex items-center gap-1.5 mb-4">
                      <IndianRupee className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-green-400 font-bold text-sm">{budgetStr}</span>
                    </div>

                    {/* Additional note */}
                    {req.additional && (
                      <p className="text-slate-500 text-xs leading-relaxed mb-4 line-clamp-2 italic">
                        "{req.additional}"
                      </p>
                    )}

                    {/* CTA */}
                    {isRevealed ? (
                      <div className="bg-green-900/40 border border-green-700/50 rounded-xl px-4 py-3 flex items-center gap-3">
                        <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />
                        <div>
                          <p className="text-white font-bold text-sm">{contactData.name}</p>
                          <a
                            href={`tel:${contactData.phone}`}
                            className="text-green-400 font-semibold text-sm hover:underline"
                            data-testid={`req-phone-${req.requirement_id}`}
                          >
                            {contactData.phone}
                          </a>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleRevealContact(req)}
                        disabled={isLoading}
                        className="w-full py-2.5 bg-green-700 hover:bg-green-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-green-900/30"
                        data-testid={`req-reveal-btn-${req.requirement_id}`}
                      >
                        <Eye className="w-4 h-4" />
                        {isLoading ? 'Loading...' : user ? 'Show Contact Details' : 'Login to View Contact'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Owner CTA Banner */}
          <div className="mt-10 bg-gradient-to-r from-green-900/50 to-slate-800 border border-green-700/30 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-bold text-base" style={{fontFamily:'Outfit,sans-serif'}}>
                Are you an Owner or Broker?
              </p>
              <p className="text-slate-400 text-sm mt-0.5">
                Browse all requirements and connect with ready tenants looking for properties right now.
              </p>
            </div>
            <Link
              to="/post-requirement"
              className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-500 text-white rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-lg shadow-green-900/30 whitespace-nowrap"
              data-testid="owner-browse-requirements-btn"
            >
              Browse All Requirements <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

        </div>
      </section>

      {/* Founder's Note — hidden, will be displayed later */}
      {/* <section className="py-16 bg-white overflow-hidden" data-testid="founders-note-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-2 block">Our Story</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>A Note from Our Founder</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center lg:justify-end order-1 lg:order-none">
              <div className="relative">
                <div className="absolute -inset-3 bg-gradient-to-br from-blue-100 to-amber-100 rounded-3xl -z-10"></div>
                <img
                  src="/founder.jpg"
                  alt="Kiran Patel - Founder, Rent Mitra"
                  className="w-72 h-80 sm:w-80 sm:h-96 object-cover object-top rounded-3xl shadow-2xl"
                  data-testid="founder-photo"
                />
                <div className="absolute -bottom-4 -right-4 bg-blue-700 text-white px-5 py-3 rounded-2xl shadow-xl">
                  <p className="font-bold text-sm" style={{fontFamily:'Outfit,sans-serif'}}>Kiran Patel</p>
                  <p className="text-blue-200 text-xs">Founder, Rent Mitra</p>
                </div>
              </div>
            </div>
            <div className="order-2 lg:order-none">
              <div className="relative">
                <span className="absolute -top-6 -left-4 text-8xl text-blue-100 font-serif leading-none select-none">"</span>
                <div className="relative space-y-4">
                  <p className="text-slate-700 text-base leading-relaxed">
                    At Rent Mitra, our vision is simple — to make property renting <span className="font-semibold text-slate-900">transparent, efficient, and stress-free</span> for everyone.
                  </p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    With over a decade of experience in the real estate industry, I have closely observed the challenges people face while renting a property — from lack of trust to unclear pricing and time-consuming processes. Rent Mitra was born to solve these problems with a customer-first approach.
                  </p>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    We are not just a platform; we are your trusted partner in finding the right space — whether it's a home, office, or investment rental. Our goal is to bridge the gap between property owners and tenants by offering verified listings, professional support, and seamless transactions.
                  </p>
                  <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-3 my-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-3">At Rent Mitra, we believe</p>
                    {[
                      { icon: '🤝', text: 'Trust is the foundation of every deal' },
                      { icon: '🔍', text: 'Transparency builds long-term relationships' },
                      { icon: '✨', text: 'Simplicity creates better experiences' },
                    ].map((b) => (
                      <div key={b.text} className="flex items-center gap-3">
                        <span className="text-lg w-7 flex-shrink-0 text-center">{b.icon}</span>
                        <span className="text-slate-700 text-sm font-medium">{b.text}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    We are committed to redefining rental experiences in India by combining <span className="font-semibold text-slate-900">technology with human understanding.</span>
                  </p>
                  <div className="pt-2">
                    <p className="font-bold text-slate-900 text-lg" style={{fontFamily:'Outfit,sans-serif'}}>Kiran Patel</p>
                    <p className="text-blue-700 text-sm font-semibold">Founder & CEO, Rent Mitra</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Social Media Section */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold uppercase tracking-widest text-blue-700 mb-2 block">Stay Connected</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{fontFamily:'Outfit,sans-serif'}}>Follow Us on Social Media</h2>
            <p className="text-slate-500 text-sm mt-2">Stay updated with the latest listings, tips, and rental news</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            {/* Facebook Card */}
            <a
              href="https://www.facebook.com/profile.php?id=61577412006699"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-5 bg-white border-2 border-slate-100 hover:border-[#1877F2] rounded-2xl px-8 py-6 shadow-sm hover:shadow-xl hover:shadow-blue-100 transition-all duration-300 w-full sm:w-72 active:scale-95"
              data-testid="home-facebook-card"
            >
              <div className="w-14 h-14 bg-[#1877F2] rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-200">
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-slate-900 text-base group-hover:text-[#1877F2] transition-colors" style={{fontFamily:'Outfit,sans-serif'}}>Rent Mitra</p>
                <p className="text-slate-500 text-sm">Follow on Facebook</p>
                <p className="text-xs text-[#1877F2] font-semibold mt-1 flex items-center gap-1">
                  facebook.com <ArrowRight className="w-3 h-3" />
                </p>
              </div>
            </a>

            {/* Instagram Card */}
            <a
              href="https://www.instagram.com/rantmitrahub/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-5 bg-white border-2 border-slate-100 hover:border-[#e6683c] rounded-2xl px-8 py-6 shadow-sm hover:shadow-xl hover:shadow-pink-100 transition-all duration-300 w-full sm:w-72 active:scale-95"
              data-testid="home-instagram-card"
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-pink-200" style={{background:'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)'}}>
                <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-slate-900 text-base group-hover:text-[#e6683c] transition-colors" style={{fontFamily:'Outfit,sans-serif'}}>@rantmitrahub</p>
                <p className="text-slate-500 text-sm">Follow on Instagram</p>
                <p className="text-xs text-[#e6683c] font-semibold mt-1 flex items-center gap-1">
                  instagram.com <ArrowRight className="w-3 h-3" />
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* List Property CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-3xl p-10 md:p-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/30 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{fontFamily:'Outfit,sans-serif'}}>
                List Your Property for FREE
              </h2>
              <p className="text-blue-200 mb-4 max-w-xl mx-auto">
                Reach thousands of tenants across India. List your property in minutes and get inquiries directly.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {['No brokerage fees', 'Direct tenant contact', 'Easy management dashboard'].map(f => (
                  <div key={f} className="flex items-center gap-1.5 text-blue-100 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400" />{f}
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate('/add-property')}
                className="bg-amber-500 hover:bg-amber-400 text-white px-8 py-3.5 rounded-xl font-bold text-base transition-colors shadow-lg inline-flex items-center gap-2"
                data-testid="list-property-cta"
              >
                List Your Property <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      {latestBlogs.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-3">
                  <BookOpen className="w-3.5 h-3.5" /> From the Blog
                </div>
                <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Real Estate Insights
                </h2>
              </div>
              <Link to="/blog" className="hidden sm:flex items-center gap-1.5 text-blue-700 font-semibold text-sm hover:text-blue-800 transition-colors" data-testid="home-blog-view-all">
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {latestBlogs.map(blog => {
                const date = new Date(blog.published_at || blog.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                return (
                  <Link key={blog.blog_id} to={`/blog/${blog.slug}`}
                    className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
                    data-testid={`home-blog-card-${blog.blog_id}`}>
                    <div className="relative h-44 bg-slate-100 overflow-hidden">
                      {blog.cover_image
                        ? <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        : <div className="w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center"><BookOpen className="w-10 h-10 text-blue-300" /></div>
                      }
                      {blog.category && (
                        <span className="absolute top-3 left-3 bg-white/90 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                          {blog.category.replace('-', ' ')}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-slate-900 text-base line-clamp-2 mb-2 group-hover:text-blue-700 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {blog.title}
                      </h3>
                      <p className="text-slate-500 text-sm line-clamp-2 mb-3">{blog.excerpt}</p>
                      <div className="flex items-center gap-3 text-slate-400 text-xs">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{date}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{blog.read_time || 1} min read</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="text-center mt-6 sm:hidden">
              <Link to="/blog" className="inline-flex items-center gap-1.5 text-blue-700 font-semibold text-sm">
                View All Articles <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
